import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { Badge, Card, CardBody, CardHeader, CardTitle, Col, Media, Row, Spinner, Table } from "reactstrap";
import { Calendar, TrendingDown, TrendingUp, Activity } from "react-feather";

import { apiFetch, API } from "../../services/api";

// Tendances d'achat : rythme des conversions par mois, par jour, par jour de
// semaine et par jour du mois (début / milieu / fin). Données : GET
// /sales-trends (table sales_daily_stats, une ligne par jour, réécrite chaque
// matin par la lambda sales-daily-stats). Les profils hebdo / jour du mois
// sont calculés sur 12 mois hors novembre (Black Friday) et corrigés du jour
// de semaine : un 15 tombant un dimanche ne passe pas pour un jour fort.

const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

const fmtInt = (n) => (n == null ? "—" : Math.round(n).toLocaleString("fr-FR"));
const fmtEur = (n) => (n == null ? "—" : Math.round(n).toLocaleString("fr-FR") + " €");
const fmtPct = (r) => (r == null ? "—" : (r > 0 ? "+" : "") + Math.round(r * 100) + " %");
const fmtIdx = (i) => (i == null || i === 0 ? "—" : (i >= 1 ? "+" : "") + Math.round((i - 1) * 100) + " %");
const monthLabel = (m) => MONTHS[parseInt(m.slice(5, 7), 10) - 1] + " " + m.slice(2, 4);
const dayLabel = (d) => { const t = new Date(d + "T00:00:00Z"); return t.getUTCDate() + " " + MONTHS[t.getUTCMonth()]; };
const tone = (r) => (r == null ? "secondary" : r >= 0.03 ? "success" : r <= -0.03 ? "danger" : "secondary");

function Kpi({ icon: Icon, color, label, value, hint }) {
    return (
        <Col md="3" sm="6">
            <Card className="flex-fill">
                <CardBody className="py-4">
                    <Media>
                        <div className="d-inline-block mt-2 mr-3">
                            <Icon className={"feather-lg text-" + color} />
                        </div>
                        <Media body>
                            <h3 className="mb-2">{value}</h3>
                            <div className="mb-0">{label}</div>
                            {hint ? <div className="text-muted small">{hint}</div> : null}
                        </Media>
                    </Media>
                </CardBody>
            </Card>
        </Col>
    );
}

export function SalesTrends() {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(90);

    useEffect(() => {
        setLoading(true);
        apiFetch(API.back + "/sales-trends?months=13&days=" + days, { method: "GET" })
            .then((r) => (r.ok ? r.json() : r.json().then((b) => Promise.reject(new Error(b.message || r.status)))))
            .then((d) => { setData(d); setError(""); })
            .catch((e) => setError(String(e.message || e)))
            .finally(() => setLoading(false));
    }, [days]);

    const months = useMemo(() => (data ? data.months : []), [data]);
    const current = data ? data.current : null;

    const monthChart = useMemo(() => ({
        series: [
            { name: "Ventes / jour", type: "column", data: months.map((m) => m.perDay) },
            { name: "Commission (k€)", type: "line", data: months.map((m) => Math.round(m.commission / 100) / 10) },
        ],
        options: {
            chart: { toolbar: { show: false } },
            colors: ["#3B7DDD", "#1F2937"],
            stroke: { width: [0, 3], curve: "smooth" },
            plotOptions: { bar: { columnWidth: "55%", borderRadius: 3 } },
            dataLabels: { enabled: true, enabledOnSeries: [0], formatter: (v) => Math.round(v), style: { fontSize: "11px" } },
            labels: months.map((m) => monthLabel(m.month)),
            yaxis: [
                { title: { text: "Ventes / jour" }, labels: { formatter: (v) => Math.round(v) } },
                { opposite: true, title: { text: "Commission (k€)" }, labels: { formatter: (v) => Math.round(v) + " k€" } },
            ],
            tooltip: { shared: true, intersect: false },
            legend: { position: "top" },
        },
    }), [months]);

    const dayChart = useMemo(() => {
        const d = data ? data.days : [];
        return {
            series: [
                { name: "Ventes", type: "area", data: d.map((x) => x.sales) },
                { name: "Moyenne 7 j", type: "line", data: d.map((x) => x.ma7) },
            ],
            options: {
                chart: { toolbar: { show: false }, zoom: { enabled: false } },
                colors: ["#3B7DDD", "#1F2937"],
                stroke: { width: [1.5, 3], curve: ["straight", "smooth"] },
                fill: { type: ["gradient", "solid"], gradient: { opacityFrom: 0.35, opacityTo: 0.05 } },
                dataLabels: { enabled: false },
                labels: d.map((x) => x.day),
                xaxis: { type: "category", labels: { rotate: 0, formatter: (v) => (v && (v.endsWith("-01") || v.endsWith("-15")) ? dayLabel(v) : "") } },
                yaxis: { labels: { formatter: (v) => Math.round(v) } },
                tooltip: { shared: true, x: { formatter: (v, o) => { const x = d[o.dataPointIndex]; return x ? WEEKDAYS[x.weekday - 1] + " " + dayLabel(x.day) : v; } } },
                legend: { position: "top" },
            },
        };
    }, [data]);

    const weekdayChart = useMemo(() => {
        const w = data ? data.weekdays : [];
        return {
            series: [{ name: "Ventes / jour", data: w.map((x) => x.avg) }],
            options: {
                chart: { toolbar: { show: false } },
                colors: [({ dataPointIndex }) => (w[dataPointIndex] && w[dataPointIndex].index >= 1.05 ? "#28a745" : w[dataPointIndex] && w[dataPointIndex].index <= 0.95 ? "#dc3545" : "#3B7DDD")],
                plotOptions: { bar: { columnWidth: "55%", borderRadius: 4, distributed: true } },
                dataLabels: { enabled: true, formatter: (v, o) => Math.round(v) + " (" + fmtIdx(w[o.dataPointIndex].index) + ")", style: { fontSize: "11px", colors: ["#1F2937"] }, offsetY: -18 },
                legend: { show: false },
                xaxis: { categories: WEEKDAYS },
                yaxis: { labels: { formatter: (v) => Math.round(v) } },
                tooltip: { y: { formatter: (v, o) => Math.round(v) + " ventes/j · min " + w[o.dataPointIndex].min + " · max " + w[o.dataPointIndex].max } },
            },
        };
    }, [data]);

    const domChart = useMemo(() => {
        const d = data ? data.dayOfMonth.filter((x) => x.n > 0) : [];
        return {
            series: [{ name: "Écart à la normale", data: d.map((x) => Math.round((x.index - 1) * 100)) }],
            options: {
                chart: { toolbar: { show: false } },
                colors: [({ value }) => (value >= 5 ? "#28a745" : value <= -5 ? "#dc3545" : "#9AA5B1")],
                plotOptions: { bar: { columnWidth: "70%", borderRadius: 2, distributed: true } },
                dataLabels: { enabled: false },
                legend: { show: false },
                xaxis: { categories: d.map((x) => x.day), title: { text: "Jour du mois" } },
                yaxis: { labels: { formatter: (v) => (v > 0 ? "+" : "") + Math.round(v) + " %" } },
                tooltip: { y: { formatter: (v, o) => (v > 0 ? "+" : "") + Math.round(v) + " % · " + d[o.dataPointIndex].avg + " ventes/j en moyenne" } },
                annotations: { yaxis: [{ y: 0, borderColor: "#1F2937" }] },
            },
        };
    }, [data]);

    const hours = useMemo(() => (data ? data.hours || [] : []), [data]);
    const hasAudience = !!(data && data.audienceDays > 0);

    const hourChart = useMemo(() => ({
        series: [
            { name: "Visiteurs distincts / h", type: "column", data: hours.map((h) => Math.round(h.visitors)) },
            { name: "Ventes / jour à cette heure", type: "line", data: hours.map((h) => h.sales) },
            { name: "Ventes pour 100 visiteurs", type: "line", data: hours.map((h) => h.conversion) },
        ],
        options: {
            chart: { toolbar: { show: false } },
            colors: ["#C7D2FE", "#3B7DDD", "#28a745"],
            stroke: { width: [0, 3, 2], curve: "smooth", dashArray: [0, 0, 4] },
            plotOptions: { bar: { columnWidth: "70%", borderRadius: 2 } },
            dataLabels: { enabled: false },
            labels: hours.map((h) => h.hour + " h"),
            yaxis: [
                { seriesName: "Visiteurs distincts / h", title: { text: "Visiteurs / h" }, labels: { formatter: (v) => Math.round(v) }, show: hasAudience },
                { seriesName: "Ventes / jour à cette heure", opposite: true, title: { text: "Ventes / j" }, labels: { formatter: (v) => v.toFixed(1) } },
                { seriesName: "Ventes pour 100 visiteurs", opposite: true, show: false, labels: { formatter: (v) => v.toFixed(2) } },
            ],
            tooltip: { shared: true, intersect: false, y: [{ formatter: (v) => Math.round(v) + " visiteurs" }, { formatter: (v) => v.toFixed(1) + " ventes/j" }, { formatter: (v) => v.toFixed(2) + " pour 100 visiteurs" }] },
            legend: { position: "top" },
        },
    }), [hours, hasAudience]);

    const heatChart = useMemo(() => {
        const cells = data ? data.hourWeekday || [] : [];
        const byDay = WEEKDAYS.map((label, i) => ({ name: label, data: cells.filter((c) => c.weekday === i + 1).map((c) => ({ x: c.hour + " h", y: Math.round(c.sales * 10) / 10, v: c.visitors })) }));
        return {
            series: byDay.slice().reverse(),
            options: {
                chart: { toolbar: { show: false } },
                colors: ["#3B7DDD"],
                dataLabels: { enabled: false },
                plotOptions: { heatmap: { radius: 2, shadeIntensity: 0.6, colorScale: { min: 0 } } },
                xaxis: { labels: { rotate: 0, formatter: (v) => (parseInt(v, 10) % 3 === 0 ? v : "") } },
                tooltip: { y: { formatter: (v, o) => { const c = o.w.config.series[o.seriesIndex].data[o.dataPointIndex]; return v + " ventes en moyenne" + (c && c.v ? " · " + Math.round(c.v) + " visiteurs" : ""); } } },
                legend: { show: false },
            },
        };
    }, [data]);

    const peakHours = useMemo(() => hours.slice().sort((a, b) => b.sales - a.sales).slice(0, 3).map((h) => h.hour + " h"), [hours]);
    const bestConv = useMemo(() => hours.filter((h) => h.visitors >= 50).sort((a, b) => b.conversion - a.conversion).slice(0, 3).map((h) => h.hour + " h (" + h.conversion.toFixed(1) + ")"), [hours]);

    return (
        <div>
            <Row className="mb-2 mb-xl-4">
                <Col xs="auto" className="d-none d-sm-block">
                    <h3>Tendances d'achat</h3>
                    <div className="text-muted">
                        Conversions (hors annulées) de <code>transaction_reporting</code>, agrégées chaque matin par jour.
                        {data ? " Dernier jour complet : " + dayLabel(data.to) + "." : ""}
                    </div>
                </Col>
            </Row>

            {error ? <div className="alert alert-danger">{error}</div> : null}
            {loading && !data ? <Spinner /> : null}

            {current ? (
                <Row>
                    <Kpi icon={Calendar} color="primary" label={"Mois en cours (" + monthLabel(current.month) + ")"} value={fmtInt(current.monthToDate)} hint={current.daysElapsed + " jours écoulés · projection " + fmtInt(current.projected)} />
                    <Kpi icon={Activity} color="info" label="Rythme 7 derniers jours" value={fmtInt(current.perDay7) + " / j"} hint={"vs " + fmtInt(current.perDayPrev28) + " / j les 4 semaines d'avant"} />
                    <Kpi icon={current.trend >= 0 ? TrendingUp : TrendingDown} color={tone(current.trend)} label="Tendance court terme" value={fmtPct(current.trend)} hint="7 j vs 28 j précédents" />
                    <Kpi icon={TrendingUp} color={tone(months.length ? months[months.length - 1].yoy : null)} label="vs même mois N-1" value={months.length ? fmtPct(months[months.length - 1].yoy) : "—"} hint="ventes / jour" />
                </Row>
            ) : null}

            <Card>
                <CardHeader><CardTitle tag="h5" className="mb-0">Par mois · ventes par jour et commission</CardTitle></CardHeader>
                <CardBody>
                    {months.length ? <Chart options={monthChart.options} series={monthChart.series} type="line" height={320} /> : null}
                    <Table size="sm" responsive className="mt-3 mb-0">
                        <thead>
                            <tr><th>Mois</th><th className="text-right">Ventes</th><th className="text-right">/ jour</th><th className="text-right">Commission</th><th className="text-right">Com. / vente</th><th className="text-right">Code promo</th><th className="text-right">Mobile</th><th className="text-right">ES</th><th className="text-right">Visiteurs / j</th><th className="text-right">Ventes / 100 vis.</th><th className="text-right">vs M-1</th><th className="text-right">vs N-1</th></tr>
                        </thead>
                        <tbody>
                            {months.map((m) => (
                                <tr key={m.month}>
                                    <td>{monthLabel(m.month)}{m.days < 28 ? <span className="text-muted"> ({m.days} j)</span> : null}</td>
                                    <td className="text-right">{fmtInt(m.sales)}</td>
                                    <td className="text-right font-weight-bold">{fmtInt(m.perDay)}</td>
                                    <td className="text-right">{fmtEur(m.commission)}</td>
                                    <td className="text-right">{m.commissionPerSale.toFixed(2)} €</td>
                                    <td className="text-right">{Math.round(m.voucherShare * 100)} %</td>
                                    <td className="text-right">{Math.round(m.mobileShare * 100)} %</td>
                                    <td className="text-right">{Math.round(m.esShare * 100)} %</td>
                                    <td className="text-right">{m.audienceDays ? fmtInt(m.visitorsPerDay) + (m.audienceDays < m.days ? " *" : "") : "—"}</td>
                                    <td className="text-right">{m.audienceDays ? m.conversion.toFixed(2) : "—"}</td>
                                    <td className="text-right"><Badge color={tone(m.mom)}>{fmtPct(m.mom)}</Badge></td>
                                    <td className="text-right"><Badge color={tone(m.yoy)}>{fmtPct(m.yoy)}</Badge></td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <div className="text-muted small mt-1">Visiteurs = anon_id distincts par jour (bots exclus, Athena) ; * = audience relevée sur une partie du mois seulement.</div>
                </CardBody>
            </Card>

            <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                    <CardTitle tag="h5" className="mb-0">Par jour · {days} derniers jours</CardTitle>
                    <div>
                        {[30, 90, 180, 365].map((n) => (
                            <Badge key={n} color={n === days ? "primary" : "light"} pill className="ml-1" style={{ cursor: "pointer" }} onClick={() => setDays(n)}>{n} j</Badge>
                        ))}
                    </div>
                </CardHeader>
                <CardBody>{data ? <Chart options={dayChart.options} series={dayChart.series} type="line" height={300} /> : null}</CardBody>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle tag="h5" className="mb-0">Heure par heure · {days} derniers jours (heure de Paris)</CardTitle>
                    <div className="text-muted small">
                        Personnes touchées (visiteurs distincts par heure, bots exclus) et ventes par heure de conversion.
                        {data && hasAudience ? " Audience relevée sur " + data.audienceDays + " jours depuis le " + dayLabel(data.audienceFrom) + "." : " Audience non encore relevée sur cette fenêtre."}
                    </div>
                </CardHeader>
                <CardBody>
                    {data ? <Chart options={hourChart.options} series={hourChart.series} type="line" height={320} /> : null}
                    {hours.length ? (
                        <Row className="mt-2">
                            <Col md="6"><div className="border rounded p-2"><div className="text-muted small">Heures qui vendent le plus</div><h5 className="mb-0">{peakHours.join(" · ")}</h5></div></Col>
                            <Col md="6"><div className="border rounded p-2"><div className="text-muted small">Meilleure conversion (ventes pour 100 visiteurs)</div><h5 className="mb-0">{bestConv.length ? bestConv.join(" · ") : "—"}</h5></div></Col>
                        </Row>
                    ) : null}
                    <div className="mt-3 text-muted small">Jour de semaine × heure · ventes moyennes par occurrence du jour</div>
                    {data ? <Chart options={heatChart.options} series={heatChart.series} type="heatmap" height={260} /> : null}
                </CardBody>
            </Card>

            <Row>
                <Col lg="5">
                    <Card>
                        <CardHeader><CardTitle tag="h5" className="mb-0">Jour de la semaine</CardTitle><div className="text-muted small">Ventes/jour moyennes sur 12 mois hors novembre · écart à la moyenne de la semaine</div></CardHeader>
                        <CardBody>{data ? <Chart options={weekdayChart.options} series={weekdayChart.series} type="bar" height={280} /> : null}</CardBody>
                    </Card>
                </Col>
                <Col lg="7">
                    <Card>
                        <CardHeader><CardTitle tag="h5" className="mb-0">Jour du mois</CardTitle><div className="text-muted small">Écart à la normale, corrigé du jour de semaine · 12 mois hors novembre</div></CardHeader>
                        <CardBody>
                            {data ? (
                                <Row className="mb-3">
                                    {data.monthThirds.map((t) => (
                                        <Col key={t.label} xs="4">
                                            <div className="border rounded p-2 text-center">
                                                <div className="text-muted small">Du {t.label}</div>
                                                <h4 className={"mb-0 text-" + tone(t.index - 1)}>{fmtIdx(t.index)}</h4>
                                                <div className="small">{Math.round(t.avg)} ventes/j</div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            ) : null}
                            {data ? <Chart options={domChart.options} series={domChart.series} type="bar" height={220} /> : null}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default SalesTrends;
